import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield,
  Truck,
  Lock,
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { CountdownTimer } from '@/components/countdown-timer'
import { AddToCartButton } from '@/components/cart/add-to-cart-button'
import { VariantSelector } from '@/components/product/variant-selector'
import { CustomInputFields, areCustomInputsComplete } from '@/components/product/custom-input-fields'
import { QuantitySelector } from '@/components/product/quantity-selector'
import { ProductMediaGallery } from '@/components/product/product-media-gallery'
import { ShareButton } from '@/components/share-button'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { getProductReviewVideo, getPrimaryProductImage } from '@/lib/product-media'
import { BRAND_NAME } from '@/lib/branding'
import { useRevealOnScroll } from '@/hooks/use-reveal-on-scroll'
import type { Product, ProductOption, ProductVariant, CustomInputDefinition } from '@/types/database'

// ─── Autoplay Hero Video ────────────────────────────────────────────

function getVideoId(url: string): { platform: 'youtube' | 'vimeo' | null; id: string | null } {
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  const youtubeMatch = url.match(youtubeRegex)
  if (youtubeMatch) return { platform: 'youtube', id: youtubeMatch[1] }
  const vimeoRegex = /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|video\/|)(\d+)/
  const vimeoMatch = url.match(vimeoRegex)
  if (vimeoMatch) return { platform: 'vimeo', id: vimeoMatch[1] }
  return { platform: null, id: null }
}

function getAutoplayEmbedUrl(platform: 'youtube' | 'vimeo', id: string): string {
  if (platform === 'youtube') {
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&cc_load_policy=0&fs=0&disablekb=1&showinfo=0&enablejsapi=1&origin=${window.location.origin}`
  }
  if (platform === 'vimeo') {
    return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1&controls=0`
  }
  return ''
}

interface HeroAutoplayVideoProps {
  videoUrl: string
  title: string
}

function HeroAutoplayVideo({ videoUrl, title }: HeroAutoplayVideoProps) {
  const { platform, id } = getVideoId(videoUrl)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isMuted, setIsMuted] = useState(true)

  if (!platform || !id) return null

  const embedUrl = getAutoplayEmbedUrl(platform, id)

  const toggleMute = () => {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow) return

    if (platform === 'youtube') {
      const func = isMuted ? 'unMute' : 'mute'
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func, args: '' }),
        'https://www.youtube.com'
      )
    } else if (platform === 'vimeo') {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'setVolume', value: isMuted ? 1 : 0 }),
        'https://player.vimeo.com'
      )
    }

    setIsMuted(prev => !prev)
  }

  return (
    <div className="hero-video-container rounded-2xl shadow-2xl shadow-red-500/10 ring-1 ring-white/10">
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={`${title} Review`}
        allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen={false}
        className="absolute inset-0 h-full w-full"
      />
      {/* Subtle gradient overlay for legibility */}
      <div className="hero-video-overlay" />
      {/* Mute/Unmute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        className="unmute-btn absolute bottom-3 right-3 rounded-full bg-black/50 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
      >
        {isMuted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
      </button>
    </div>
  )
}

// ─── Featured Hero ──────────────────────────────────────────────────

interface FeaturedHeroProps {
  product: Product
  className?: string
}

export function FeaturedHero({ product, className }: FeaturedHeroProps) {
  const [showMobileCTA, setShowMobileCTA] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null)
  const [customInputValues, setCustomInputValues] = useState<Record<string, string>>({})
  const [quantity, setQuantity] = useState(1)

  const effectivePrice = product.original_price + (selectedVariant?.price_adjustment || 0)
  const discountedPrice = effectivePrice * (1 - product.featured_discount_percent / 100)
  const savingsAmount = effectivePrice - discountedPrice
  const keyFeatures = useMemo(
    () => (Array.isArray(product.key_features) ? (product.key_features as string[]).filter(Boolean).slice(0, 6) : []),
    [product.key_features]
  )

  const options = useMemo(
    () => (product.options as unknown as ProductOption[]) || [],
    [product.options]
  )
  const hasOptions = options.some(o => o.values.length > 0)
  const variants = useMemo(
    () => (product.variants as unknown as ProductVariant[]) || [],
    [product.variants]
  )
  const customInputDefs = useMemo(
    () => (product.custom_inputs as unknown as CustomInputDefinition[]) || [],
    [product.custom_inputs]
  )
  const hasCustomInputs = customInputDefs.length > 0

  const needsVariant = hasOptions && !selectedVariant
  const needsInputs = hasCustomInputs && !areCustomInputsComplete(customInputDefs, customInputValues)
  const addToCartDisabled = needsVariant || (selectedVariant ? !selectedVariant.available : false) || needsInputs

  // Review video for the cinematic hero
  const reviewVideo = useMemo(() => getProductReviewVideo(product), [product])
  const heroFallbackImage = useMemo(() => getPrimaryProductImage(product), [product])

  // Share data
  const shareUrl = `${window.location.origin}/review/${product.slug}`
  const shareTitle = `${product.title} - ${product.featured_discount_percent}% Off | ${BRAND_NAME}`
  const shareDescription = product.short_description || `Check out this week's deal on ${product.title}`

  // Scroll reveal refs
  const dealSectionRef = useRevealOnScroll<HTMLDivElement>(0.08)
  const featuresRef = useRevealOnScroll<HTMLDivElement>(0.15)

  // Ref for scroll-to-deal
  const dealAreaRef = useRef<HTMLElement>(null)

  const scrollToDeal = useCallback(() => {
    dealAreaRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > 520
      setShowMobileCTA(previous => (previous === shouldShow ? previous : shouldShow))
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      {/* Section 1: Cinematic Video Hero */}
      <section className={cn('relative overflow-hidden bg-zinc-950 pb-0 pt-10 lg:pt-16', className)}>
        {/* Animated gradient blobs */}
        <div className="pointer-events-none absolute inset-0">
          <div className="animate-float-blob absolute -left-32 -top-20 h-[500px] w-[500px] rounded-full bg-red-600/8 blur-[100px]" />
          <div className="animate-float-blob-reverse absolute -bottom-20 -right-32 h-[500px] w-[500px] rounded-full bg-zinc-600/15 blur-[100px]" />
          <div className="animate-float-blob absolute left-1/2 top-1/4 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/5 blur-[80px]" />
        </div>

        <div className="container relative mx-auto px-4">
          {/* Badge — glassmorphic with live indicator */}
          <div className="hero-enter mb-6 flex items-center justify-center lg:mb-8">
            <span className="badge-glow-pulse inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm font-semibold uppercase tracking-widest text-white backdrop-blur-sm">
              <span className="size-1.5 animate-pulse rounded-full bg-red-500" />
              This Week&apos;s Review
            </span>
          </div>

          {/* Video / Hero Image */}
          <div className="hero-enter hero-enter-d1 mx-auto max-w-5xl">
            {reviewVideo ? (
              <HeroAutoplayVideo videoUrl={reviewVideo.url} title={product.title} />
            ) : heroFallbackImage ? (
              <div className="overflow-hidden rounded-2xl shadow-2xl shadow-red-500/10 ring-1 ring-white/10">
                <img
                  src={heroFallbackImage}
                  alt={`${product.title} featured`}
                  className="aspect-video w-full object-cover"
                />
              </div>
            ) : (
              <div className="flex aspect-video items-center justify-center rounded-2xl bg-zinc-900 ring-1 ring-white/10">
                <span className="text-sm text-zinc-500">Media coming soon</span>
              </div>
            )}
          </div>

          {/* Title + Description */}
          <div className="hero-enter hero-enter-d2 mx-auto mt-8 max-w-3xl text-center lg:mt-10">
            <h1 className="text-3xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              {product.title}
            </h1>
            {product.short_description && (
              <p className="mt-3 text-base text-zinc-400 md:mt-4 md:text-lg">
                {product.short_description}
              </p>
            )}
          </div>

          {/* Price + Countdown + Scroll CTA */}
          <div className="mt-6 flex flex-col items-center gap-4">
            {/* Price teaser pill */}
            <div className="hero-enter hero-enter-d3 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-sm">
              <span className="text-sm text-zinc-500 line-through">${effectivePrice.toFixed(2)}</span>
              <span className="text-2xl font-bold text-white">${discountedPrice.toFixed(2)}</span>
              <span className="rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-bold text-white">
                {product.featured_discount_percent}% OFF
              </span>
            </div>

            {/* Hero Countdown Timer — glassmorphic */}
            {product.featured_end_date && (
              <div className="hero-enter hero-enter-d4 flex flex-col items-center">
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-white/40">
                  Deal ends in
                </p>
                <CountdownTimer targetDate={product.featured_end_date} size="md" variant="hero" />
              </div>
            )}

            {/* Scroll CTA */}
            <button
              type="button"
              onClick={scrollToDeal}
              className="hero-enter hero-enter-d5 group inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-white"
            >
              Get This Deal
              <ArrowDown className="size-4 animate-bounce" />
            </button>
          </div>
        </div>

        {/* Dark-to-light gradient transition (tall, smooth) */}
        <div
          className="pointer-events-none relative mt-10 h-32 lg:mt-14 lg:h-40"
          style={{
            background: 'linear-gradient(to bottom, rgb(9 9 11) 0%, rgb(9 9 11 / 0.8) 25%, rgb(9 9 11 / 0.4) 50%, rgb(250 250 250 / 0.6) 75%, rgb(250 250 250) 100%)',
          }}
        />
      </section>

      {/* Section 2: Deal Purchase Area */}
      <section ref={dealAreaRef} className="relative bg-zinc-50 pb-10 pt-2 lg:pb-14 lg:pt-4" style={{ scrollMarginTop: '5rem' }}>
        <div className="container relative mx-auto px-4">
          <div
            ref={dealSectionRef}
            className="reveal-fade-up grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start"
          >
            {/* Product Gallery (all media including review video for browsing) */}
            <ProductMediaGallery
              product={product}
              selectedVariant={selectedVariant}
              variant="hero"
            />

            {/* Deal Card */}
            <div className="space-y-5">
              <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-md">
                {/* Price Block */}
                <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                  <span className="text-xl text-zinc-400 line-through">${effectivePrice.toFixed(2)}</span>
                  <span className="text-4xl font-bold text-zinc-900">${discountedPrice.toFixed(2)}</span>
                  <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                    {product.featured_discount_percent}% OFF
                  </span>
                </div>
                <p className="mt-2 text-sm font-semibold text-emerald-600">You save ${savingsAmount.toFixed(2)}</p>

                {/* Countdown */}
                {product.featured_end_date && (
                  <div className="mt-5 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Deal ends in</p>
                    <CountdownTimer targetDate={product.featured_end_date} size="sm" variant="light" />
                  </div>
                )}

                {/* Variant Selector */}
                {hasOptions && (
                  <div className="mt-5">
                    <VariantSelector
                      options={options}
                      variants={variants}
                      selectedVariantId={selectedVariant?.id || null}
                      onVariantChange={setSelectedVariant}
                    />
                  </div>
                )}

                {/* Custom Inputs */}
                {hasCustomInputs && (
                  <div className="mt-4">
                    <p className="mb-2 text-sm font-medium text-zinc-700">Personalization</p>
                    <CustomInputFields
                      definitions={customInputDefs}
                      values={customInputValues}
                      onChange={setCustomInputValues}
                    />
                  </div>
                )}

                {/* Quantity + CTA */}
                <div className="mt-5 space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-zinc-700">Qty</label>
                    <QuantitySelector value={quantity} onChange={setQuantity} size="sm" />
                  </div>
                  <AddToCartButton
                    productId={product.id}
                    variantId={selectedVariant?.id ?? null}
                    quantity={quantity}
                    customInputs={hasCustomInputs ? customInputValues : undefined}
                    disabled={addToCartDisabled}
                    size="lg"
                    className="cta-glow h-12 w-full text-base font-semibold md:text-lg"
                  >
                    {needsVariant
                      ? `Select Options - $${discountedPrice.toFixed(2)}`
                      : `Add to Cart - $${discountedPrice.toFixed(2)}`}
                  </AddToCartButton>
                </div>

                {/* Share */}
                <div className="mt-4">
                  <ShareButton
                    url={shareUrl}
                    title={shareTitle}
                    description={shareDescription}
                    variant="button"
                    className="w-full"
                  />
                </div>

                {/* Trust Signals */}
                <div className="mt-4 grid grid-cols-1 gap-2 text-xs text-zinc-500 sm:grid-cols-3">
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-50 px-2.5 py-2">
                    <Lock className="size-3.5" />
                    Secure checkout
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-50 px-2.5 py-2">
                    <Truck className="size-3.5" />
                    Free shipping
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-50 px-2.5 py-2">
                    <Shield className="size-3.5" />
                    Buyer protection
                  </div>
                </div>

                {/* Full Details Link */}
                <div className="mt-4 border-t border-zinc-200 pt-3 text-center">
                  <Link
                    to={`/review/${product.slug}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900"
                  >
                    View full product details
                    <ArrowRight className="size-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features (below the grid, scroll-reveal with stagger) */}
          {keyFeatures.length > 0 && (
            <div ref={featuresRef} className="reveal-stagger mt-8">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {keyFeatures.map(feature => (
                  <div
                    key={feature}
                    className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-sm text-zinc-700 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <CheckCircle2 className="size-3.5 text-emerald-600" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div
        className={cn(
          'sticky-cta transform transition-transform duration-300 md:hidden',
          showMobileCTA ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="line-clamp-1 text-sm font-semibold text-zinc-900">{product.title}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400 line-through">${effectivePrice.toFixed(2)}</span>
              <span className="text-lg font-bold text-zinc-900">${discountedPrice.toFixed(2)}</span>
            </div>
          </div>
          <ShareButton
            url={shareUrl}
            title={shareTitle}
            description={shareDescription}
            variant="icon"
          />
          <AddToCartButton
            productId={product.id}
            variantId={selectedVariant?.id ?? null}
            quantity={quantity}
            customInputs={hasCustomInputs ? customInputValues : undefined}
            disabled={addToCartDisabled}
            className="h-10 shrink-0 px-4 font-semibold"
          >
            {needsVariant ? 'Select Options' : 'Get Deal'}
          </AddToCartButton>
        </div>
      </div>
    </>
  )
}

// ─── Empty State ────────────────────────────────────────────────────

interface FeaturedHeroEmptyProps {
  className?: string
}

export function FeaturedHeroEmpty({ className }: FeaturedHeroEmptyProps) {
  return (
    <section
      className={cn(
        'relative flex items-center overflow-hidden bg-zinc-50 py-16 lg:py-24',
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-zinc-100 to-transparent" />
        <div className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-red-100/40 blur-3xl" />
        <div className="absolute -right-24 bottom-8 h-72 w-72 rounded-full bg-zinc-200/60 blur-3xl" />
      </div>

      <div className="container relative mx-auto flex w-full px-4">
        <div className="mx-auto w-full max-w-2xl rounded-3xl border border-zinc-200/80 bg-white/85 p-8 text-center shadow-sm backdrop-blur md:p-12">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Weekly Drop Schedule
          </p>
          <h1 className="mb-4 text-3xl font-bold text-zinc-900 md:text-4xl lg:text-5xl">
            No Deal This Week
          </h1>
          <p className="mx-auto mb-8 max-w-xl text-lg text-zinc-600">
            New featured reviews are published weekly. Explore past reviews now
            and check back for the next drop.
          </p>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/past-reviews">Browse Past Reviews</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/about">How It Works</Link>
            </Button>
          </div>

          <p className="mt-6 text-xs text-zinc-500">
            Featured drops rotate automatically when new products are scheduled.
          </p>
        </div>
      </div>
    </section>
  )
}
